import yesPlanner from "../assets/logos/yes-planner.jpeg";
import nerdma from "../assets/projects/nerdma-systems.png";
import arteco from "../assets/projects/arteco-global.png";

const note = ({ slug, date, image, name, title, excerpt, purpose, technology, features, history }) => ({
  slug,
  date,
  readingTime: 4,
  categories: ["Projects", "Development"],
  image,
  imageAlt: { en: `${name} project preview`, fr: `Aperçu du projet ${name}` },
  relatedProject: { name, href: "/#projects" },
  title: { en: title, fr: title },
  excerpt: { en: excerpt, fr: excerpt },
  content: {
    en: [
      { type: "lead", text: purpose },
      { type: "heading", text: "What I used" },
      { type: "paragraph", text: technology },
      { type: "heading", text: "What the project needed" },
      { type: "paragraph", text: features },
      { type: "heading", text: "How the work evolved" },
      { type: "paragraph", text: history },
      { type: "quote", text: "The goal was not only to make the interface look finished, but to make the product easier to understand and use." },
    ],
    fr: [
      { type: "lead", text: purpose },
      { type: "heading", text: "Technologies utilisées" },
      { type: "paragraph", text: technology },
      { type: "heading", text: "Les besoins du projet" },
      { type: "paragraph", text: features },
      { type: "heading", text: "L'évolution du travail" },
      { type: "paragraph", text: history },
      { type: "quote", text: "L'objectif n'était pas seulement de terminer l'interface, mais de rendre le produit plus clair et plus simple à utiliser." },
    ],
  },
});

export const projectNotesData = [
  note({
    slug: "yes-planner-build-notes",
    date: "2026-10-30",
    image: yesPlanner,
    name: "Yes Planner",
    title: "Building a calmer way to plan a wedding",
    excerpt: "The story behind Yes Planner: a venue-led wedding platform designed to make early planning feel less overwhelming.",
    purpose: "Yes Planner is a wedding-planning platform built around a simple first question: where could this celebration take place? The experience leads with venues so couples can move from inspiration to practical planning without being buried in a long checklist.",
    technology: "The project uses Angular and responsive component patterns, with SEO and performance treated as part of the product rather than as final clean-up work. The structure keeps venue information, calls to action and supporting content easy to scan on smaller screens.",
    features: "The most important features are the venue-led presentation, responsive layouts, clear information hierarchy and direct paths to continue exploring. The design aims to feel romantic and premium without sacrificing clarity.",
    history: "The work started by clarifying what the visitor should understand first. Once the venue experience became the centre of the page, the visual language, content order and responsive behaviour could support that decision instead of competing with it.",
  }),
  note({
    slug: "nerdma-systems-build-notes",
    date: "2026-10-30",
    image: nerdma,
    name: "Nerdma Systems",
    title: "Making a technical company easier to understand",
    excerpt: "How I shaped a responsive Angular website around clarity, search visibility and maintainable content sections.",
    purpose: "Nerdma Systems needed a public website that could explain its work quickly to people who were not already familiar with the company. The project was less about adding visual noise and more about making technical value easy to understand.",
    technology: "I used Angular, TypeScript and responsive component patterns, with attention to semantic content, search visibility and maintainable page structure. The implementation keeps repeated sections consistent while leaving room for the content to grow.",
    features: "The core features are a clear service hierarchy, responsive navigation, structured content sections and focused calls to action. Each section has a job: explain, build confidence or help the visitor continue.",
    history: "The work evolved through repeated content and layout refinement. Instead of treating the website as a collection of isolated screens, I worked from the questions a visitor would ask and arranged the interface around those answers.",
  }),
  note({
    slug: "arteco-global-build-notes",
    date: "2026-10-30",
    image: arteco,
    name: "Arteco Global",
    title: "Connecting real-time video systems reliably",
    excerpt: "A practical look at backend integration work involving video events, AI camera platforms and secure device communication.",
    purpose: "Arteco Global involved connecting systems that need to communicate reliably while events are happening in real time. The work sits behind the interface, where dependable message handling and secure communication matter more than visual polish.",
    technology: "The integration work used C# and .NET services, JWT-based security and real-time APIs. The focus was on creating clear boundaries between systems so incoming events, device communication and application behaviour could be reasoned about separately.",
    features: "The important features were secure authentication, real-time event handling and dependable communication with external camera and AI platforms. These capabilities need careful failure handling because a missed event can affect what an operator sees.",
    history: "The project reinforced the value of understanding the full journey of an event: where it begins, how it is authenticated, how it moves through the system and how the final application responds. That history shaped the integration decisions more than any individual endpoint.",
  }),
];
